import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Car, Plus, Trash2, MapPin, RotateCcw, Fuel, Calendar, DollarSign, RefreshCw, X } from "lucide-react";
import { toast } from "sonner";
import { API as API_BASE, authConfig } from "./constants";

const API = API_BASE.replace(/\/api$/, "");

const FUEL_TYPES = {
  diesel: { label: "Diesel", color: "#E52020" },
  unleaded_91: { label: "91", color: "#16A34A" },
  unleaded_95: { label: "95", color: "#DC2626" },
};

const FREQ_LABELS = {
  daily: "Daily",
  weekly: "Weekly",
  custom: "Custom",
};

export default function CommutesPanel({ user, fuelPrices, regionalPrices, prefillRoute, onPrefillConsumed }) {
  const [commutes, setCommutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    name: "",
    origin: "",
    destination: "",
    distance_km: "",
    consumption_l_per_100km: "8",
    fuel_type: "diesel",
    region: "national",
    return_trip: true,
    frequency: "daily",
    days_per_week: 5,
  });

  const fetchCommutes = useCallback(async () => {
    try {
      const resp = await axios.get(`${API}/api/commutes/`, authConfig());
      setCommutes(resp.data);
    } catch (err) {
      console.error("Error fetching commutes:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user) fetchCommutes();
    else setLoading(false);
  }, [user, fetchCommutes]);

  useEffect(() => {
    if (prefillRoute && user) {
      setForm((prev) => ({
        ...prev,
        origin: prefillRoute.origin || "",
        destination: prefillRoute.destination || "",
        distance_km: String(prefillRoute.distance_km || ""),
        name: `${prefillRoute.origin || "?"} → ${prefillRoute.destination || "?"}`,
        consumption_l_per_100km: prefillRoute.consumption || prev.consumption_l_per_100km,
        fuel_type: prefillRoute.fuel_type || prev.fuel_type,
        return_trip: prefillRoute.return_trip !== undefined ? prefillRoute.return_trip : prev.return_trip,
      }));
      setShowForm(true);
      if (onPrefillConsumed) onPrefillConsumed();
      setTimeout(() => {
        const el = document.querySelector('[data-testid="commute-form"]');
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 100);
    }
  }, [prefillRoute, user, onPrefillConsumed]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name || !form.distance_km || parseFloat(form.distance_km) <= 0) {
      toast.error("Please fill in commute name and distance");
      return;
    }
    try {
      await axios.post(`${API}/api/commutes/`, {
        ...form,
        distance_km: parseFloat(form.distance_km),
        consumption_l_per_100km: parseFloat(form.consumption_l_per_100km),
        waypoints: null,
      }, authConfig());
      toast.success("Commute saved!");
      setShowForm(false);
      setForm({
        name: "", origin: "", destination: "", distance_km: "",
        consumption_l_per_100km: "8", fuel_type: "diesel", region: "national",
        return_trip: true, frequency: "daily", days_per_week: 5,
      });
      fetchCommutes();
    } catch (err) {
      toast.error("Failed to save commute");
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/api/commutes/${id}`, authConfig());
      setCommutes(commutes.filter((c) => c.id !== id));
      toast.success("Commute removed");
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  const calcCommuteCost = (commute) => {
    const price = getRegionalPrice(commute.fuel_type, commute.region);
    const effectiveDist = commute.return_trip ? commute.distance_km * 2 : commute.distance_km;
    const litres = (effectiveDist / 100) * commute.consumption_l_per_100km;
    return {
      perTrip: (litres * price).toFixed(2),
      perWeek: (litres * price * commute.days_per_week).toFixed(2),
      perMonth: (litres * price * commute.days_per_week * 4.33).toFixed(2),
      litresPerTrip: litres.toFixed(1),
      effectiveDist,
      price,
    };
  };

  const getRegionalPrice = (fuelType, region) => {
    if (regionalPrices) {
      const r = regionalPrices.find((rp) => rp.region_key === region);
      if (r) {
        if (fuelType === "unleaded_91") return r.unleaded_91;
        if (fuelType === "unleaded_95") return r.unleaded_95;
        return r.diesel;
      }
    }
    if (fuelPrices) {
      if (fuelType === "unleaded_91") return fuelPrices.unleaded_91;
      if (fuelType === "unleaded_95") return fuelPrices.unleaded_95;
      return fuelPrices.diesel;
    }
    return 3.60;
  };

  const sfInput = "w-full bg-[rgba(229, 32, 32,0.03)] border border-[rgba(229, 32, 32,0.12)] rounded-sm px-3 py-2.5 text-sm text-[#1A1A1A] placeholder-[#475569] outline-none transition-all focus:border-[#00F0FF] focus:shadow-[0_0_10px_rgba(229, 32, 32,0.15)] font-['Manrope']";
  const sfSelect = "w-full bg-[#FFFFFF] border border-[rgba(229, 32, 32,0.12)] rounded-sm px-3 py-2.5 text-sm text-[#1A1A1A] outline-none transition-all focus:border-[#00F0FF] font-['Manrope'] cursor-pointer";

  if (!user) {
    return (
      <div className="sf-card page-enter delay-300">
        <div className="p-6 sm:p-8 text-center">
          <Car className="w-10 h-10 mx-auto mb-3 text-[#E52020] opacity-25" />
          <h2 className="sf-subheading mb-2">MY COMMUTES</h2>
          <p className="text-sm text-[#666666] font-['Manrope']">Sign in to save commutes and track costs</p>
        </div>
      </div>
    );
  }

  return (
    <div className="sf-card page-enter delay-300">
      <div className="p-6 sm:p-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="sf-subheading flex items-center gap-2">
            <Car className="w-5 h-5 text-[#E52020]" />
            MY COMMUTES
          </h2>
          <div className="flex items-center gap-2">
            <button onClick={fetchCommutes} className="text-[#666666] hover:text-[#E52020] transition-colors p-1.5 rounded-sm hover:bg-[rgba(229, 32, 32,0.06)]" title="Refresh prices">
              <RefreshCw className="w-4 h-4" />
            </button>
            <button onClick={() => setShowForm(!showForm)} className="text-sm bg-[#E52020] text-white hover:shadow-[0_4px_12px_rgba(229, 32, 32,0.3)] transition-all px-3 py-1.5 rounded-sm flex items-center gap-1 font-bold font-['Manrope'] tracking-wider" data-testid="add-commute-btn">
              {showForm ? <X className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
              {showForm ? "Cancel" : "Add"}
            </button>
          </div>
        </div>

        {showForm && (
          <form onSubmit={handleSave} className="space-y-3 mb-6 p-4 bg-[rgba(229, 32, 32,0.02)] border border-[rgba(0,240,255,0.08)] rounded-sm" data-testid="commute-form">
            <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Commute name (e.g. Daily Work Drive)" className={sfInput} data-testid="commute-name-input" required />
            <div className="grid grid-cols-2 gap-3">
              <input type="text" value={form.origin} onChange={(e) => setForm({ ...form, origin: e.target.value })} placeholder="From" className={sfInput} data-testid="commute-origin-input" />
              <input type="text" value={form.destination} onChange={(e) => setForm({ ...form, destination: e.target.value })} placeholder="To" className={sfInput} data-testid="commute-destination-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input type="number" value={form.distance_km} onChange={(e) => setForm({ ...form, distance_km: e.target.value })} placeholder="Distance (km)" className={sfInput} data-testid="commute-distance-input" required min="0.1" step="0.1" />
              <input type="number" value={form.consumption_l_per_100km} onChange={(e) => setForm({ ...form, consumption_l_per_100km: e.target.value })} placeholder="L/100km" className={sfInput} data-testid="commute-consumption-input" min="0.1" step="0.1" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <select value={form.fuel_type} onChange={(e) => setForm({ ...form, fuel_type: e.target.value })} className={sfSelect} data-testid="commute-fuel-select">
                <option value="diesel">Diesel</option>
                <option value="unleaded_91">Unleaded 91</option>
                <option value="unleaded_95">Unleaded 95</option>
              </select>
              <select value={form.frequency} onChange={(e) => setForm({ ...form, frequency: e.target.value })} className={sfSelect} data-testid="commute-freq-select">
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="custom">Custom</option>
              </select>
              <input type="number" value={form.days_per_week} onChange={(e) => setForm({ ...form, days_per_week: parseInt(e.target.value) || 1 })} placeholder="Days/week" className={sfInput} min="1" max="7" data-testid="commute-days-input" />
            </div>
            <label className="flex items-center gap-2 text-sm text-[#1A1A1A] cursor-pointer font-['Manrope']">
              <input type="checkbox" checked={form.return_trip} onChange={(e) => setForm({ ...form, return_trip: e.target.checked })} className="accent-[#00F0FF]" />
              <RotateCcw className="w-3.5 h-3.5 text-[#E52020]" />
              Return trip (round trip)
            </label>
            <button type="submit" className="w-full bg-[#E52020] text-white py-2.5 rounded-md text-sm font-bold transition-all hover:bg-[#CC1B1B] hover:shadow-[0_4px_12px_rgba(229, 32, 32,0.3)] font-['Manrope'] tracking-wider" data-testid="save-commute-btn">
              SAVE COMMUTE
            </button>
          </form>
        )}

        {loading ? (
          <div className="space-y-3">{[1, 2].map((i) => <div key={i} className="sf-skeleton h-24 rounded-sm" />)}</div>
        ) : commutes.length === 0 && !showForm ? (
          <div className="text-center py-8 text-[#666666]">
            <Car className="w-10 h-10 mx-auto mb-3 opacity-20" />
            <p className="font-['Manrope']">No commutes saved yet</p>
            <p className="text-xs mt-1 font-mono">Add your regular drives to track costs</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="commutes-list">
            {commutes.map((commute) => {
              const costs = calcCommuteCost(commute);
              return (
                <div key={commute.id} className="group p-4 bg-[rgba(229, 32, 32,0.02)] border border-[rgba(0,240,255,0.08)] rounded-sm hover:border-[rgba(229, 32, 32,0.15)] transition-all" data-testid="commute-item">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className="font-semibold text-[#1A1A1A] text-sm font-['Manrope']">{commute.name}</h3>
                      {(commute.origin || commute.destination) && (
                        <p className="text-xs text-[#666666] flex items-center gap-1 mt-0.5 font-mono">
                          <MapPin className="w-3 h-3 text-[#E52020]" />
                          {commute.origin || "?"} → {commute.destination || "?"}
                        </p>
                      )}
                    </div>
                    <button onClick={() => handleDelete(commute.id)} className="p-1.5 opacity-0 group-hover:opacity-100 transition-all text-[#666666] hover:text-[#FF0055] hover:bg-[rgba(255,0,85,0.06)] rounded-sm" data-testid="delete-commute-btn">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    <span className="text-xs px-2 py-0.5 rounded-sm bg-[rgba(229, 32, 32,0.06)] border border-[rgba(229, 32, 32,0.1)] text-[#1A1A1A] font-mono">{costs.effectiveDist} km</span>
                    {commute.return_trip && <span className="sf-badge-green text-xs">Return</span>}
                    <span className="text-xs px-2 py-0.5 rounded-sm font-mono font-medium" style={{ color: FUEL_TYPES[commute.fuel_type]?.color, background: `${FUEL_TYPES[commute.fuel_type]?.color}10`, border: `1px solid ${FUEL_TYPES[commute.fuel_type]?.color}30` }}>
                      {FUEL_TYPES[commute.fuel_type]?.label || commute.fuel_type}
                    </span>
                    <span className="text-xs px-2 py-0.5 rounded-sm bg-[rgba(229, 32, 32,0.06)] border border-[rgba(229, 32, 32,0.1)] text-[#666666] font-mono">{FREQ_LABELS[commute.frequency]} · {commute.days_per_week}d/wk</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div className="text-center p-2 bg-[rgba(229, 32, 32,0.03)] border border-[rgba(229, 32, 32,0.06)] rounded-sm">
                      <span className="block text-[10px] text-[#666666] mb-1 font-['Manrope'] tracking-wider">PER TRIP</span>
                      <span className="text-sm font-bold text-[#1A1A1A] font-mono">${costs.perTrip}</span>
                    </div>
                    <div className="text-center p-2 bg-[rgba(229, 32, 32,0.03)] border border-[rgba(229, 32, 32,0.06)] rounded-sm">
                      <span className="block text-[10px] text-[#666666] mb-1 font-['Manrope'] tracking-wider">PER WEEK</span>
                      <span className="text-sm font-bold text-[#1A1A1A] font-mono">${costs.perWeek}</span>
                    </div>
                    <div className="text-center p-2 bg-[rgba(229, 32, 32,0.06)] border border-[rgba(229, 32, 32,0.15)] rounded-sm">
                      <span className="block text-[10px] text-[#E52020] mb-1 font-['Manrope'] tracking-wider">PER MONTH</span>
                      <span className="text-sm font-bold text-[#E52020] font-mono">${costs.perMonth}</span>
                    </div>
                  </div>
                  <p className="text-xs text-[#475569] mt-2 font-mono">
                    @ ${costs.price.toFixed(2)}/L · {costs.litresPerTrip}L per trip
                  </p>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
